import React, { useEffect, useState } from "react";
import { Form, Input, Select, Checkbox } from "antd";
import { useLazyQuery, useQuery } from "@apollo/client";
import { isEmpty } from "lodash";
import useLang from "../../../../hooks/useLang";
import { REVIEW_PLATFORM_TYPES } from "../../../../constants/reviewPlatforms";
import LocationSelect from "../../../commonComponents/LocationSelect";
import { GET_DEPARTMENTS, GET_OPPORTUNITTIES_TYPES } from "../../../../graphql/query";

const { Option } = Select;

const ReviewPlatformDrawerForm = ({
  form,
  editableRecord = null,
  company,
  onSubmit, // Callback to parent
  defaultLocationId = null,
}) => {
  const [lang] = useLang();
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [googleConnected, setGoogleConnected] = useState(false);
  const [facebookConnected, setFacebookConnected] = useState(false);
  const [isPrimary, setIsPrimary] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState(null);

  const { data: opportunityTypesData } = useQuery(GET_OPPORTUNITTIES_TYPES);
  const [fetchDepartments, { data: departmentsData }] = useLazyQuery(GET_DEPARTMENTS, {
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (selectedLocationId) {
      fetchDepartments({
        variables: { locationId: +selectedLocationId, companyId: company?.id },
      });
    }
  }, [selectedLocationId, company?.id, fetchDepartments]);

  const platformSources = [
    {
      key: REVIEW_PLATFORM_TYPES.GOOGLE,
      value: REVIEW_PLATFORM_TYPES.GOOGLE,
      label: lang.google,
    },
    {
      key: REVIEW_PLATFORM_TYPES.FACEBOOK,
      value: REVIEW_PLATFORM_TYPES.FACEBOOK,
      label: lang.facebook,
    },
    {
      key: REVIEW_PLATFORM_TYPES.YELP,
      value: REVIEW_PLATFORM_TYPES.YELP,
      label: lang.yelp,
    },
    {
      key: REVIEW_PLATFORM_TYPES.DEALER_RATER,
      value: REVIEW_PLATFORM_TYPES.DEALER_RATER,
      label: lang.delearRater,
    },
    {
      key: REVIEW_PLATFORM_TYPES.CUSTOM_SITE,
      value: REVIEW_PLATFORM_TYPES.CUSTOM_SITE,
      label: lang.customSite,
    },
  ];

  // Set initial values when editableRecord changes
  useEffect(() => {
    if (editableRecord) {
      setSelectedPlatform(editableRecord?.platform);
      setIsPrimary(editableRecord?.isPrimary || false);
      setGoogleConnected(editableRecord?.isOauthConnected || false);
      setFacebookConnected(editableRecord?.isOauthConnected || false);
      setSelectedLocationId(editableRecord?.locationId || null);

      const formValues = {
        locationId: editableRecord?.locationId?.toString(),
        platform: editableRecord?.platform,
        departmentId: editableRecord?.departmentId || null,
        opportunityTypeId: editableRecord?.opportunityTypeId || null,
      };

      // Set platform-specific values
      if (editableRecord?.platform === REVIEW_PLATFORM_TYPES.GOOGLE) {
        formValues.googleReviewLink = editableRecord?.reviewPostUrl || "";
      } else if (editableRecord?.platform === REVIEW_PLATFORM_TYPES.FACEBOOK) {
        formValues.facebookReviewLink = editableRecord?.reviewPostUrl || "";
      } else if (editableRecord?.platform === REVIEW_PLATFORM_TYPES.YELP) {
        formValues.yelpBusinessProfileLink = editableRecord?.platformUrl || "";
        formValues.yelpBusinessFormLink = editableRecord?.reviewPostUrl || "";
      } else if (editableRecord?.platform === REVIEW_PLATFORM_TYPES.DEALER_RATER) {
        formValues.dealerRaterAccessToken = editableRecord?.accessToken || "";
        formValues.dealerRaterDealerId = editableRecord?.accountId || "";
        formValues.dealerRaterFormLink = editableRecord?.reviewPostUrl || "";
      } else if (editableRecord?.platform === REVIEW_PLATFORM_TYPES.CUSTOM_SITE) {
        formValues.customSiteName = editableRecord?.siteName || "";
        formValues.customReviewLink = editableRecord?.reviewPostUrl || "";
      }

      form.setFieldsValue(formValues);
    } else {
      // Reset form for new record
      form.resetFields();
      setSelectedPlatform(null);
      setIsPrimary(true); // Default to true in create mode
      setGoogleConnected(false);
      setFacebookConnected(false);
      setSelectedLocationId(defaultLocationId || null);

      // Set default locationId if provided
      if (defaultLocationId) {
        form.setFieldsValue({
          locationId: defaultLocationId.toString(),
        });
      }
    }
  }, [editableRecord, form, defaultLocationId]);

  const handlePlatformChange = value => {
    setSelectedPlatform(value);
  };

  // Form submit handler - Just collect values and pass to parent
  const onFinish = values => {
    // Pass all form values and component state to parent
    onSubmit({
      values,
      editableRecord,
      googleConnected,
      facebookConnected,
      isPrimary,
    });
  };

  return (
    <Form form={form} layout="vertical" className="relative" onFinish={onFinish}>
      {/* Location */}
      <Form.Item
        name="locationId"
        label={<span className="form-field-label">{lang.location}</span>}
        rules={[{ required: true, message: lang.selectLocationError }]}
        className="mb-5"
      >
        <LocationSelect
          variables={{ companyId: company?.id }}
          skip={!company?.id}
          allowClear
          placeholder="Select Location"
          disabled={!isEmpty(editableRecord)}
          onChange={value => {
            setSelectedLocationId(value ? +value : null);
            form.setFieldsValue({ departmentId: null });
          }}
        />
      </Form.Item>

      {/* Opportunity Type (optional — scopes the row to this opp type) */}
      <Form.Item
        name="opportunityTypeId"
        label={<span className="form-field-label">Opportunity type</span>}
        className="mb-5"
      >
        <Select
          placeholder="Applies to all opportunity types"
          allowClear
          disabled={!isEmpty(editableRecord)}
        >
          {(opportunityTypesData?.getOpportunityTypes || [])
            .filter(t => t.isActive)
            .map(opType => (
              <Option key={opType.id} value={opType.id}>
                {opType.parentType ? `${opType.parentType.name} / ${opType.name}` : opType.name}
              </Option>
            ))}
        </Select>
      </Form.Item>

      {/* Department (optional — scopes the row to this department) */}
      <Form.Item
        name="departmentId"
        label={<span className="form-field-label">Department</span>}
        className="mb-5"
      >
        <Select
          placeholder="Applies to all departments"
          allowClear
          disabled={!isEmpty(editableRecord) || !selectedLocationId}
        >
          {(departmentsData?.getDepartments || [])
            .filter(d => d.isActive)
            .map(dept => (
              <Option key={dept.id} value={dept.id}>
                {dept.name}
              </Option>
            ))}
        </Select>
      </Form.Item>

      {/* Review Platform */}
      <Form.Item
        name="platform"
        label={<span className="form-field-label">{lang.reviewSource}</span>}
        rules={[{ required: true, message: lang.selectReviewSourceError }]}
        className="mb-5"
      >
        <Select
          placeholder={lang.selectReviewSource}
          onChange={handlePlatformChange}
          disabled={!isEmpty(editableRecord)}
        >
          {platformSources.map(platform => (
            <Option key={platform.key} value={platform.value}>
              {platform.label}
            </Option>
          ))}
        </Select>
      </Form.Item>

      {/* Google Fields */}
      {selectedPlatform === REVIEW_PLATFORM_TYPES.GOOGLE && (
        <>
          <Form.Item
            name="googleReviewLink"
            label={<span className="form-field-label">{lang.googleReviewLink}</span>}
            rules={[
              { required: true, message: `Please enter ${lang.googleReviewLink?.toLowerCase()}` },
            ]}
            className="mb-5"
          >
            <Input placeholder={lang.googleReviewLink} />
          </Form.Item>
          <Form.Item>
            <Checkbox
              checked={googleConnected}
              onChange={e => setGoogleConnected(e.target.checked)}
            >
              {lang.connectAccountToGoogle}
            </Checkbox>
          </Form.Item>
        </>
      )}

      {/* Facebook Fields */}
      {selectedPlatform === REVIEW_PLATFORM_TYPES.FACEBOOK && (
        <>
          <Form.Item
            name="facebookReviewLink"
            label={<span className="form-field-label">{lang.facebookReviewLink}</span>}
            rules={[
              { required: true, message: `Please enter ${lang.facebookReviewLink?.toLowerCase()}` },
            ]}
            className="mb-5"
          >
            <Input placeholder={lang.facebookReviewLink} />
          </Form.Item>
          <Form.Item>
            <Checkbox
              checked={facebookConnected}
              onChange={e => setFacebookConnected(e.target.checked)}
            >
              {lang.connectAccountToFacebook}
            </Checkbox>
          </Form.Item>
        </>
      )}

      {/* Yelp Fields */}
      {selectedPlatform === REVIEW_PLATFORM_TYPES.YELP && (
        <>
          <Form.Item
            name="yelpBusinessProfileLink"
            label={<span className="form-field-label">{lang.yelpBusinessProfileLink}</span>}
            rules={[
              {
                required: true,
                message: `Please enter ${lang.yelpBusinessProfileLink?.toLowerCase()}`,
              },
            ]}
            className="mb-5"
          >
            <Input placeholder={lang.yelpBusinessProfileLink} />
          </Form.Item>
          <Form.Item
            name="yelpBusinessFormLink"
            label={<span className="form-field-label">{lang.yelpReviewFormLink}</span>}
            rules={[
              { required: true, message: `Please enter ${lang.yelpReviewFormLink?.toLowerCase()}` },
            ]}
            className="mb-5"
          >
            <Input placeholder={lang.yelpReviewFormLink} />
          </Form.Item>
        </>
      )}

      {/* Dealer Rater Fields */}
      {selectedPlatform === REVIEW_PLATFORM_TYPES.DEALER_RATER && (
        <>
          <Form.Item
            name="dealerRaterAccessToken"
            label={<span className="form-field-label">{lang.delearRaterAccessToken}</span>}
            rules={[
              {
                required: true,
                message: `Please enter ${lang.delearRaterAccessToken?.toLowerCase()}`,
              },
            ]}
            className="mb-5"
          >
            <Input placeholder={lang.delearRaterAccessToken} />
          </Form.Item>
          <Form.Item
            name="dealerRaterDealerId"
            label={<span className="form-field-label">{lang.delearRaterId}</span>}
            rules={[
              { required: true, message: `Please enter ${lang.delearRaterId?.toLowerCase()}` },
            ]}
            className="mb-5"
          >
            <Input placeholder={lang.delearRaterId} />
          </Form.Item>
          <Form.Item
            name="dealerRaterFormLink"
            label={<span className="form-field-label">{lang.delearRaterRevieLink}</span>}
            rules={[
              {
                required: true,
                message: `Please enter ${lang.delearRaterRevieLink?.toLowerCase()}`,
              },
            ]}
            className="mb-5"
          >
            <Input placeholder={lang.delearRaterRevieLink} />
          </Form.Item>
        </>
      )}

      {/* Custom Site Fields */}
      {selectedPlatform === REVIEW_PLATFORM_TYPES.CUSTOM_SITE && (
        <>
          <Form.Item
            name="customSiteName"
            label={<span className="form-field-label">Site Name</span>}
            rules={[{ required: true, message: "Please enter Site Name" }]}
            className="mb-5"
          >
            <Input placeholder="Enter site name" />
          </Form.Item>
          <Form.Item
            name="customReviewLink"
            label={<span className="form-field-label">Site Review Link</span>}
            rules={[{ required: true, message: "Please enter Site Review Link" }]}
            className="mb-5"
          >
            <Input placeholder="Enter site review link" />
          </Form.Item>
        </>
      )}

      {/* Make Primary Checkbox */}
      {selectedPlatform && (
        <Form.Item>
          <Checkbox checked={isPrimary} onChange={e => setIsPrimary(e.target.checked)}>
            Make this review site preferred
          </Checkbox>
        </Form.Item>
      )}
    </Form>
  );
};

export default ReviewPlatformDrawerForm;
